import {useEffect, useRef, useState, useCallback} from "react";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const getTurnstileSiteKey = (): string => {
  return import.meta.env.PUBLIC_TURNSTILE_SITE_KEY || "";
};

interface UseTurnstileOptions {
  action?: string;
}

interface UseTurnstileReturn {
  turnstileRef: (node: HTMLDivElement | null) => void;
  token: string | null;
  isReady: boolean;
  error: string | null;
  reset: () => void;
  enabled: boolean;
}

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existing = document.querySelector(
      `script[src*="challenges.cloudflare.com/turnstile"]`,
    );
    if (existing) {
      // Script tag exists but not loaded yet — poll
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      return;
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      // Turnstile sets window.turnstile after script load
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    };
    script.onerror = () => reject(new Error("Failed to load Turnstile script"));
    document.head.appendChild(script);
  });
}

export function useTurnstile(
  options?: UseTurnstileOptions,
): UseTurnstileReturn {
  const siteKey = getTurnstileSiteKey();
  const enabled = !!siteKey;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const scriptLoadedRef = useRef(false);
  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(!enabled);
  const [error, setError] = useState<string | null>(null);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || !enabled) return;

    // Remove existing widget if any
    if (widgetIdRef.current !== null) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (_) {
        /* ignore */
      }
      widgetIdRef.current = null;
    }

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: "dark",
        size: "flexible",
        appearance: "interaction-only",
        action: options?.action,
        callback: (t: string) => {
          setToken(t);
          setIsReady(true);
          setError(null);
        },
        "expired-callback": () => {
          setToken(null);
          setIsReady(false);
        },
        "error-callback": (errorCode: string) => {
          console.error("[Turnstile] Widget error:", errorCode);
          setToken(null);
          setIsReady(false);
          setError("Captcha failed to verify. Please refresh the page.");
        },
      });
    } catch (e) {
      console.error("[Turnstile] Render failed:", e);
      setError("Captcha failed to load. Please refresh the page.");
    }
  }, [enabled, siteKey, options?.action]);

  const reset = useCallback(() => {
    setToken(null);
    setIsReady(!enabled);
    setError(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (_) {
        /* ignore */
      }
    }
  }, [enabled]);

  // Callback ref — renders widget as soon as the DOM element mounts
  const turnstileRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (node && enabled && scriptLoadedRef.current && window.turnstile) {
        renderWidget();
      }
    },
    [enabled, renderWidget],
  );

  // Load the Turnstile script once
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled) return;
        scriptLoadedRef.current = true;
        renderWidget();
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[Turnstile]", err.message);
        setError(
          "Captcha script failed to load. Check your network or ad blocker.",
        );
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [enabled, renderWidget]);

  return {turnstileRef, token, isReady, error, reset, enabled};
}

// Type augmentation for the Turnstile global
declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: Record<string, unknown>,
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}
